import { z } from "zod";
import {
  commandContextSchema,
  commandError,
  stableFingerprint,
  type AuditEntry,
  type CommandContext,
  type CommandReceipt,
  type CommandResult,
  type CommandSuccess,
} from "../../lib/command-contract";
import {
  PREFERENCE_KEYS,
  PREFERENCE_SCHEMAS,
  type PreferenceKey,
  type UserPreference,
} from "./contracts";

/**
 * Comando único das preferências: gravar o valor de uma chave conhecida.
 *
 * Duas escolhas que evitam problemas conhecidos:
 *
 * - **O valor é validado pelo schema da chave antes de gravar.** Um `jsonb`
 *   aceita qualquer coisa; sem essa barreira, a próxima leitura é que descobre
 *   o problema, longe de quem causou.
 * - **A gravação substitui o valor inteiro, com `expectedVersion` opcional.**
 *   Mesclagem parcial de duas telas abertas produziria uma preferência que
 *   ninguém escolheu; a versão esperada deixa o conflito explícito.
 */

export interface PreferenceCommandState {
  preferences: UserPreference[];
  commandReceipts: CommandReceipt[];
  auditLog: AuditEntry[];
}

export interface PreferenceCommandValue {
  preference: UserPreference;
}

export interface PreferenceCommandDeps {
  now: () => string;
  uuid: () => string;
}

export interface PreferenceCommandExecution {
  result: CommandResult<PreferenceCommandValue>;
  state: PreferenceCommandState;
}

const INVALID_CONTEXT: CommandContext = Object.freeze({
  ownerId: "invalid-context",
  actorType: "system",
  actorId: "preference-command",
  origin: "system",
  commandId: "invalid-context",
  correlationId: "invalid-context",
  idempotencyKey: "invalid-context",
  requestedAt: "1970-01-01T00:00:00.000Z",
  schemaVersion: 1,
});

export const setPreferenceSchema = z.object({
  key: z.enum(PREFERENCE_KEYS),
  value: z.unknown(),
  /**
   * Versão lida pela tela; rejeita gravação sobre alteração mais nova.
   * Zero é uma expectativa legítima: a tela leu a preferência como inexistente.
   */
  expectedVersion: z.number().int().nonnegative().optional(),
});

export type SetPreferenceInput = z.input<typeof setPreferenceSchema>;

function cloneState(state: PreferenceCommandState): PreferenceCommandState {
  return {
    preferences: state.preferences.map((item) => ({ ...item })),
    commandReceipts: [...state.commandReceipts],
    auditLog: [...state.auditLog],
  };
}

function checkedContext(context: unknown): CommandContext | undefined {
  const parsed = commandContextSchema.safeParse(context);
  return parsed.success ? parsed.data : undefined;
}

function fail(
  state: PreferenceCommandState,
  context: CommandContext,
  code: Parameters<typeof commandError>[1],
  message: string,
): PreferenceCommandExecution {
  return {
    state,
    result: commandError(context, code, message, {
      severity: code === "persistence_error" ? "error" : "warning",
    }),
  };
}

function validationMessage(error: unknown): string {
  if (error instanceof z.ZodError) return error.issues[0]?.message ?? "Dados inválidos.";
  return error instanceof Error ? error.message : "Dados inválidos.";
}

export function executeSetPreference(
  stateInput: PreferenceCommandState,
  input: SetPreferenceInput,
  contextInput: CommandContext,
  deps: PreferenceCommandDeps,
): PreferenceCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof setPreferenceSchema>;
  try {
    draft = setPreferenceSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }

  const key = draft.key as PreferenceKey;
  let value: unknown;
  try {
    value = PREFERENCE_SCHEMAS[key].parse(draft.value ?? {});
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }

  const fingerprint = stableFingerprint({ key, value, expectedVersion: draft.expectedVersion });
  const state = cloneState(stateInput);

  const receipt = state.commandReceipts.find(
    (item) =>
      item.ownerId === context.ownerId &&
      item.command === "preferences.set" &&
      item.idempotencyKey === context.idempotencyKey,
  );
  if (receipt) {
    if (receipt.requestFingerprint !== fingerprint)
      return fail(
        stateInput,
        context,
        "idempotency_conflict",
        "A chave de idempotência já foi usada para outra intenção.",
      );
    if (!receipt.result.ok)
      return { state, result: receipt.result as CommandResult<PreferenceCommandValue> };
    return {
      state,
      result: {
        ...(receipt.result as CommandSuccess<PreferenceCommandValue>),
        status: "replayed",
      },
    };
  }

  const current = state.preferences.find((item) => item.key === key);
  if (
    draft.expectedVersion !== undefined &&
    (current?.version ?? 0) !== draft.expectedVersion
  )
    return fail(
      stateInput,
      context,
      "version_conflict",
      "A preferência foi alterada em outro lugar. Recarregue antes de salvar.",
    );

  const occurredAt = deps.now();
  const preference: UserPreference = {
    key,
    value,
    updatedAt: occurredAt,
    version: (current?.version ?? 0) + 1,
  };
  const preferences = [...state.preferences.filter((item) => item.key !== key), preference];

  const auditId = deps.uuid();
  const result: CommandSuccess<PreferenceCommandValue> = {
    ok: true,
    status: "applied",
    value: { preference },
    commandId: context.commandId,
    correlationId: context.correlationId,
    version: preference.version,
    eventIds: [],
    outboxJobIds: [],
    auditId,
    warnings: [],
  };
  const audit: AuditEntry = {
    id: auditId,
    ownerId: context.ownerId,
    command: "preferences.set",
    commandId: context.commandId,
    correlationId: context.correlationId,
    causationId: context.causationId,
    actorType: context.actorType,
    actorId: context.actorId,
    origin: context.origin,
    targetType: "user_preference",
    targetId: key,
    idempotencyKey: context.idempotencyKey,
    requestFingerprint: fingerprint,
    result: "applied",
    occurredAt,
    before: current ? { version: current.version } : undefined,
    after: { version: preference.version },
  };
  const newReceipt: CommandReceipt = {
    id: deps.uuid(),
    ownerId: context.ownerId,
    command: "preferences.set",
    idempotencyKey: context.idempotencyKey,
    requestFingerprint: fingerprint,
    commandId: context.commandId,
    correlationId: context.correlationId,
    result,
    createdAt: occurredAt,
    schemaVersion: context.schemaVersion,
  };

  return {
    result,
    state: {
      preferences,
      auditLog: [...state.auditLog, audit],
      commandReceipts: [...state.commandReceipts, newReceipt],
    },
  };
}

export interface PreferenceDemoAdapter {
  set(
    input: SetPreferenceInput,
    context: CommandContext,
  ): Promise<CommandResult<PreferenceCommandValue>>;
  getState(): PreferenceCommandState;
  syncExternal(state: PreferenceCommandState): void;
}

export function createPreferenceDemoAdapter(
  initialState: PreferenceCommandState,
  deps: PreferenceCommandDeps,
  onCommit?: (state: PreferenceCommandState) => void,
): PreferenceDemoAdapter {
  let state = cloneState(initialState);
  let queue: Promise<unknown> = Promise.resolve();
  let busy = false;

  const enqueue = (
    operation: () => PreferenceCommandExecution,
  ): Promise<CommandResult<PreferenceCommandValue>> => {
    const run = async () => {
      busy = true;
      try {
        const execution = operation();
        if (execution.result.ok && execution.result.status === "applied") {
          state = execution.state;
          onCommit?.(state);
        }
        return execution.result;
      } finally {
        busy = false;
      }
    };
    if (!busy) {
      const started = run();
      queue = started.then(
        () => undefined,
        () => undefined,
      );
      return started;
    }
    const next = queue.then(run);
    queue = next.then(
      () => undefined,
      () => undefined,
    );
    return next as Promise<CommandResult<PreferenceCommandValue>>;
  };

  return {
    set: (input, context) => enqueue(() => executeSetPreference(state, input, context, deps)),
    getState: () => state,
    syncExternal: (next) => {
      if (!busy) state = cloneState(next);
    },
  };
}
